import ExitEarlyAlert from '@components/auto-account-creation/exitEarlyAlert/ExitEarlyAlert';
import {
	isSemanticSearchCalledAtom,
	isSemanticSuggestionsOpenAtom,
} from '@components/listings-search-bar/atoms';
import Breadcrumbs from '@components/shared/Breadcrumbs';
import useSearchScrolledOut from '@hooks/useSearchScrolledOut';
import { isMultiOrNoCategoryUIAtom } from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { searchScrolledOutAtom } from '@pages/VendorsSearch/containers/SemanticSearch/atoms';
import { useAppSelector } from '@redux/hooks';
import { selectIsMobile } from '@redux/viewport/selectors';
import { GridColumn, GridRow } from '@tkww/orion-web-ds-structure';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { type FC, useEffect, useRef } from 'react';
import SpotlightAd from '../../SearchResults/components/SpotlightAd';
import AdStyles from '../../TopBar/styles.scss';
import { SemanticSearch } from './SemanticSearch';
import { SemanticTitle } from './SemanticTitle';
import Styles from './styles.scss';

export interface SemanticTopBarProps {
	categoryCode: Category.CategoryCode;
	isBOW: boolean;
	location: Redux.Location;
	marketCode: string;
	shouldShowSpotlight: boolean;
}

export const SemanticTopBar: FC<SemanticTopBarProps> = (props) => {
	const {
		categoryCode,
		isBOW,
		location: { city, stateCode },
		marketCode,
		shouldShowSpotlight,
	} = props;
	const isMobile = useAppSelector(selectIsMobile);
	const isSemanticSearchCalled = useAtomValue(isSemanticSearchCalledAtom);
	const isMultiOrNoCategory = useAtomValue(isMultiOrNoCategoryUIAtom);
	const shouldHideBreadcrumbs = isMultiOrNoCategory;

	// Detect when this header has scrolled out of view so the sticky filter row
	// can render a condensed search bar alongside the chips.
	const scrolledOutSentinelRef = useRef<HTMLDivElement>(null);
	const isScrolledOut = useSearchScrolledOut(scrolledOutSentinelRef);
	const setSearchScrolledOut = useSetAtom(searchScrolledOutAtom);
	const setIsSuggestionsOpen = useSetAtom(isSemanticSuggestionsOpenAtom);
	useEffect(() => {
		setSearchScrolledOut(isScrolledOut);
		// Also close the typeahead, because this transition swaps which search bar
		// is mounted. The swap unmounts the focused input so focus falls to the
		// body, while `isSemanticSuggestionsOpenAtom` is module-level and survives
		// it — leaving an open dropdown that silently drops keystrokes.
		setIsSuggestionsOpen(false);
	}, [isScrolledOut, setSearchScrolledOut, setIsSuggestionsOpen]);
	// Render off the atom, not the local hook state, so this header and the sticky
	// filter row share one source of truth. The atom is written in an effect, so
	// local state leads it by a render — enough, on scroll-back-in, to mount two
	// ListingsSearchBarConnected instances at once and duplicate the static input
	// id that `hideSearchBar` exists to prevent.
	const isSearchScrolledOut = useAtomValue(searchScrolledOutAtom);
	// Reset on unmount: this is an SPA route (react-router), so the atom's
	// value would otherwise persist across a client-side navigation and
	// briefly mislead a freshly-mounted header until the observer fires.
	useEffect(() => {
		return () => setSearchScrolledOut(false);
	}, [setSearchScrolledOut]);
	// Derive spotlight hiding from breadcrumbs to keep cross-category hiding logic coupled.
	// Both breadcrumbs and spotlight ads are hidden in cross-category multi/no-category mode
	// to reduce clutter. Deriving one from the other prevents accidental drift.
	const shouldHideSpotlight = shouldHideBreadcrumbs;
	const row = !isSemanticSearchCalled
		? Styles.listingsSectionsRow
		: Styles.semanticRow;
	const desktopRowClass = shouldHideBreadcrumbs
		? `${row} ${Styles.hiddenBreadcrumbs}`
		: row;

	const breadcrumbs = (
		<Breadcrumbs
			categoryCode={categoryCode}
			city={city}
			stateCode={stateCode}
			isHeader
		/>
	);

	return (
		// `data-new-search-bar="true"` is now permanent — this used to be
		// conditional on the `newMarketplaceSearchBarV2` experiment. Keep this
		// attribute for downstream consumers (analytics, styling, etc.).
		<div data-new-search-bar="true">
			{shouldShowSpotlight && !shouldHideSpotlight && (
				<GridRow>
					<GridColumn xs="12">
						<SpotlightAd
							marketCode={marketCode}
							categoryCode={categoryCode}
							isBOW={isBOW}
							className={AdStyles.adBanner}
						/>
					</GridColumn>
				</GridRow>
			)}
			{isMobile ? (
				<>
					<GridRow className={row}>
						<GridColumn xs="12">
							<SemanticSearch />
						</GridColumn>
					</GridRow>
					<GridRow>
						<GridColumn xs="12">
							<SemanticTitle />
						</GridColumn>
					</GridRow>
				</>
			) : (
				<>
					{!shouldHideBreadcrumbs && (
						<GridRow>
							<GridColumn xs="12">{breadcrumbs}</GridColumn>
						</GridRow>
					)}
					<GridRow className={desktopRowClass}>
						<GridColumn className={Styles.semanticColumnTitle}>
							<SemanticTitle />
						</GridColumn>
						<GridColumn className={Styles.semanticColumnSearch}>
							<SemanticSearch hideSearchBar={isSearchScrolledOut} />
						</GridColumn>
					</GridRow>
				</>
			)}
			<ExitEarlyAlert />
			{/* A zero-height sentinel can report inconsistent IntersectionObserver
			    results in some browsers; give it a minimal height so it reliably
			    intersects the viewport. */}
			<div ref={scrolledOutSentinelRef} style={{ height: 1 }} />
		</div>
	);
};
